// Convert balance — one party's box, from one asset to another (11 Sept 2026).
//
// Operator's case: a party owes the desk in AED and both sides agree it is
// now owed in USDT instead — or the desk holds their AED and will hold USDT
// for them instead. No money moves. Until now the only door was a settle-
// later trade on /trade followed by two settlements by hand — three entries
// and a sweep for what is one agreement — or a receipt and a payment through
// a rail the money never touched.
//
// It IS a trade, and it is booked as one: POST /api/trades with
// `unsettled: true` and `settle_on_account: true`, so both legs net against
// the box in the same breath, no drawer is named, and the rate sits on the
// deal for the reports. What this screen adds is the ARITHMETIC OF SIDES:
// which leg is take and which is give follows from the SIGN of the balance
// being converted, and getting it backwards doubles the position instead of
// converting it. deriveConversion() below is that rule, in one place, with
// the four cases spelled out.
//
// THE NUMBERS COME FROM THE SHARED CALCULATOR (12 Sept 2026). The first cut
// had one amount box and a rate box asking "how many AED for 1 INR" (0.044 —
// nobody quotes it that way), and the amount it becomes was a line of text.
// The operator: "wrong, calculation difficult this way — I want screens
// everywhere for asset-1, asset-2 & rate and 3-way auto calculation, that too
// dynamic which changes with each number I change in the 2nd box." So: an
// amount box per asset and a Rate box, all three live, any two filling the
// third on every keystroke or paste, the rate reading the way the desk says
// it (INR per AED, AED per USDT) whichever side is being converted.
// useTradeMath owns those three values; nothing here re-does that arithmetic.
//
// Reached from the party page ("Convert balance"); lives at
// /parties/:slug/convert so it can carry that party and be linked to.
//
// THE FORM IS A COMPONENT (12 Sept 2026, evening). The operator asked for
// "swap screen, for all customer/treasury all accounts on left side tab — to
// convert their balances": one screen, every party and every desk account
// listed, pick one, convert. That screen (screens/Swap.tsx, titled Convert
// since 14 Sept) shows THIS form for a party, so ConvertBalanceForm below is
// everything the route rendered between its header and its footer, and
// ConvertBalance() is the route's own header around it.
//
// Sweep of 14 Sept 2026 (SWEEP-SCOPE §5.8): the explaining hints went (a
// 36-character hint under a 210px column wrapped and pushed that box below
// its neighbours — "convert balance boxes are not in line"), the "Becomes"
// line went (it repeated the To box), the plan panel is the two box lines,
// Notes folded under More, the band check is the shared one
// (lib/trade-math.ts bandWords — one table for every screen with a rate),
// the client-side "converted on account" note prefix went (the BFF writes
// its own marker with the rate; nothing read either). The posting body is
// otherwise as it was.
//
// 21 Oct 2026 audit: the As-of chips stand without a FieldRow of their own.
// Left as they were, on purpose: the figures in the three boxes (the box IS
// what posts — grouping or rounding its text would change the posting) and
// the rate said both ways under Rate (it catches an inverted rate).
//
// 22 Oct 2026 design-system sweep (docs/DESIGN-SYSTEM.md sections 3 and 5): a read that failed is said in ONE amber line with the
// way out (ReadFailed) and never drawn as an empty list; a refusal after a button is its first sentence with the rest behind
// Details, a 5xx — which may have partly posted — whole (ServerLine); the three lines the other five screens of the money forms
// share are written here once (ReadFailed, ServerLine, sayFirst) and a small action carries a phone-sized hit area (TAP) — the kit
// has no such pieces yet. The guards that stop a wrong click stay as one short line with their figures behind a tap: "not
// confirmed as netted", the off-band rate (a rate the calculator WORKED OUT still says "check them" on the line). The posting
// body, every enabling rule and every refusal are as they were.

import { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, type Direction, type Monetary } from '../lib/api';
import { tg } from '../lib/telegram';
import { ASSETS, isFiat } from '../lib/workflow';
import { amountProblem, rateProblem } from '../lib/amount';
import { errorWords, isSentinel, nothingLanded, ApiError } from '../lib/errors';
import { useTradeMath } from '../hooks/useTradeMath';
import { bandWords } from '../lib/trade-math';
import { ScreenHeader } from '../components/ScreenHeader';
import { Pending } from '../components/Pending';
import { Chip } from '../components/FilterRail';
import { AssetPicker } from '../components/AssetPicker';
import { AsOfChips } from '../components/AsOfChips';
import { Field, FieldRow } from '../components/Field';
import { BoxLines, boxWords } from '../components/BoxLines';
import { ErrorLine, WarnLine } from '../components/ErrorLine';
import { MoreDetails } from '../components/MoreDetails';
import { RepeatPanel, useRepeatGuard } from '../components/RepeatPanel';
import { useDesk, type Desk } from '../components/DeskSwitch';
import { EmptyState, ReadFailed, sayFirst, ServerLine, StateLine, TAP } from '../components/kit';
import { partyName, foldAsset, sameAsset, compareRelevance } from '../lib/naming';
import { ledgerText } from '../lib/ledger-scale';
import {
  displayAsset, fmtAmount, fmtDay, fmtRate, dubaiDate,
  dubaiWallToIso, backdateProblem,
} from '../lib/format';

/** The registry's assets, FOLDED — a party's box speaks USDT, never USDT·TRC. */
const BOX_ASSETS: string[] = [...new Set(ASSETS.map((a) => foldAsset(a)))];

// rateProblem is lib/amount.ts's (sweep item 21); the band table is
// lib/trade-math.ts's (sweep item 24) — neither has a copy here any more.

export interface ConversionInput {
  /** B — the party's balance in the from asset. Positive = they owe the desk. */
  balance: number;
  fromAsset: string;
  toAsset: string;
  /** In the from asset — how much of the box is converted. */
  fromAmount: number;
  /** In the to asset — what it becomes. Both amounts come from the calculator,
   *  typed or worked out there; this function never multiplies (12 Sept). */
  toAmount: number;
  /** The calculator's rate, "quote per base" of the PAIR (AED per USDT, INR
   *  per AED) whichever side is being converted — posted as it reads. */
  rate: string;
}
export interface ConversionPlan {
  direction: Direction;
  take: Monetary;
  give: Monetary;
  /** The calculator's rate, trimmed — see deriveConversion for why the
   *  route takes it as it reads. */
  rate: string;
  /** The to amount — the figure the to box holds. */
  converted: number;
  /** +1 when they owe the desk in the from asset, −1 when the desk holds theirs. */
  sign: 1 | -1;
}

/**
 * THE FOUR CASES. The box must move by −sign(B)·fromAmount in the from asset
 * and +sign(B)·toAmount in the to asset. In the trade route's terms
 * `amount_take` is what the party PAYS (their box goes UP — they owe more) and
 * `amount_give` is what they RECEIVE (box goes DOWN).
 *
 *   B > 0 (they owe FROM)         give = FROM amount · take = TO amount
 *     FROM fiat, TO crypto  →  take crypto, give fiat  →  customer_sell
 *     FROM crypto, TO fiat  →  take fiat, give crypto  →  customer_buy
 *   B < 0 (the desk holds FROM)   take = FROM amount · give = TO amount
 *     FROM fiat, TO crypto  →  take fiat, give crypto  →  customer_buy
 *     FROM crypto, TO fiat  →  take crypto, give fiat  →  customer_sell
 *
 * THE RATE GOES THROUGH AS THE CALCULATOR READS IT (12 Sept 2026). On a
 * fiat/crypto pair the calculator's "quote per base" IS the route's fiat per
 * crypto — the reciprocal the first cut took when FROM was the fiat went
 * with the TO-per-FROM box it corrected. A fiat↔fiat or crypto↔crypto pair
 * has no fiat-per-crypto triangle and no inventory book; the route names the
 * rate's base and quote by WHICH PAIR OF AMOUNTS THE RATE DIVIDES INTO
 * (trades.ts step 12b, after the 11 Sept review found the direction-read
 * words inverted for one shape), never by the direction, so the words on the
 * deal come out right whichever way the calculator reads. The direction rule
 * for those pairs stays what it was — B > 0 → customer_buy, B < 0 →
 * customer_sell — because the operator's standing word is "don't temper with
 * operational codes": the payload's fields and their meaning do not move.
 */
export function deriveConversion(i: ConversionInput): ConversionPlan {
  const sign: 1 | -1 = i.balance > 0 ? 1 : -1;
  const fromLeg: Monetary = { asset: i.fromAsset, amount: i.fromAmount };
  const toLeg:   Monetary = { asset: i.toAsset,   amount: i.toAmount };
  const [take, give] = sign > 0 ? [toLeg, fromLeg] : [fromLeg, toLeg];
  const takeFiat = isFiat(take.asset);
  const giveFiat = isFiat(give.asset);
  const direction: Direction =
    takeFiat && !giveFiat ? 'customer_buy'
    : !takeFiat && giveFiat ? 'customer_sell'
    : sign > 0 ? 'customer_buy' : 'customer_sell';   // same-family pair: unchanged since 11 Sept, see above
  return { direction, take, give, rate: i.rate.trim(), converted: i.toAmount, sign };
}

/** The route: /parties/:slug/convert — the party page's own header around
 *  the form. Reads the party itself (the same key as the form, one fetch) so
 *  the subtitle can name them; the form does the rest. */
export function ConvertBalance() {
  const { slug } = useParams<{ slug: string }>();
  const [entity] = useDesk();
  const nav = useNavigate();

  // Same key as the party page, so arriving from it is a cache hit.
  const q = useQuery({
    queryKey: ['party', slug, entity],
    queryFn:  () => api.partyDetail(slug!, entity),
    enabled:  !!slug,
  });

  // THE SCREEN IS REPLACED ONLY WHEN THERE IS NOTHING TO SHOW (Astra, 22 Oct 2026). Every posting refreshes the party; a refresh
  // that fails over the party in hand used to unmount the form — and with it the outcome of the posting it held, "Booked, but not
  // confirmed as netted" included, which a Try again then replaced with a clean form without the guard. With the party cached the
  // form stays and says so itself (ConvertBalanceForm, beside the outcome).
  if (q.isError && !q.data) {
    return (
      <div className="space-y-3 lg:max-w-[720px] lg:mx-auto">
        <button onClick={() => nav(-1)} className="btn-link text-meta">← Back</button>
        <ReadFailed what="this party" error={q.error} onRetry={() => void q.refetch()} tone="bad" />
      </div>
    );
  }
  if (!q.data)   return <Pending />;

  const name = partyName(q.data.party);
  // A rail is not a party (the form says so, and shows no header for it).
  const ownAccount = q.data.party.party_type === 'own_account';
  return (
    <div className={`space-y-3 lg:max-w-[720px] lg:mx-auto${ownAccount ? '' : ' lg:space-y-4'}`}>
      <button onClick={() => nav(-1)} className="btn-link text-meta">← Back</button>
      {!ownAccount && (
        <ScreenHeader
          title="Convert balance"
          subtitle={`${name}'s box, one asset into another — no money moves.`}
          help={<ConvertAbout />}
        />
      )}
      <ConvertBalanceForm slug={slug!} entity={entity} />
    </div>
  );
}

/** "About this screen" for a party's conversion — the same text on the route
 *  and on the Convert screen's party panel (Swap.tsx renders it too). */
export function ConvertAbout() {
  return (
    <>
      <p>
        Booked as a trade settled on account: both legs net against the party&rsquo;s box in one go, no
        desk account is named, and the rate goes on the deal. What they owe you in one asset becomes what
        they owe you in the other; what you hold of theirs likewise. The two box lines above the button
        read it back before anything posts.
      </p>
      <p>
        Any two of the two amounts and the rate fill the third. The rate reads the desk&rsquo;s way —
        INR per AED, AED per USDT. The From amount starts as the whole balance; converting more than the
        balance opens a position the other way, and the screen says so first.
      </p>
    </>
  );
}

/** The form: from chips, to picker, the three boxes, when, notes, the
 *  posting and its answer. Renders as a fragment so whoever shows it — the
 *  route above, the Convert screen's right-hand panel — owns the box around it. */
export function ConvertBalanceForm({ slug, entity }: { slug: string; entity: Desk }) {
  const nav = useNavigate();

  // Same key as the party page, so arriving from it is a cache hit.
  const q = useQuery({
    queryKey: ['party', slug, entity],
    queryFn:  () => api.partyDetail(slug, entity),
  });

  const [fromAsset, setFromAsset] = useState<string | null>(null);
  const [toAsset, setToAsset]     = useState<string | null>(null);
  // The two amounts and the rate live in the shared calculator (12 Sept): an
  // amount box per asset, the rate "quote per base" of the pair, any two
  // filling the third. This screen never holds those three itself.
  const math = useTradeMath(fromAsset ?? '', toAsset ?? '');
  const [notes, setNotes]   = useState('');
  const [when, setWhen]     = useState('');
  const whenIso   = when ? dubaiWallToIso(when) : '';
  const whenWords = backdateProblem(when);

  // EVERY HOOK ABOVE THE DATA GUARDS (the party page's own lesson, 2 Sept):
  // a hook below an early return changes the hook count between renders and
  // takes the whole console down.
  const balances = q.data?.balances ?? {};
  // A REFRESH THAT FAILED keeps the last good read beside the error (TanStack v5): the chips may stay on screen, but nothing is
  // CLAIMED from them and nothing is posted off them — Appendix B. This is the one place "in error" becomes "unknown"; the plan
  // (which reads the balance's sign for the direction), the over-balance warning, the empty claim and the post-anyway doors
  // below all follow it. It matters most right after a posting: the form then holds a box that PREDATES it, and converting again
  // from it would double the position, not convert it. The outcome stays on screen and Try again is the way on. A first read that
  // has not answered never gets here (the data guard returns Pending).
  const unknown = q.isError;
  // The boxes that can be converted: every asset this party's balance is not
  // zero on, most relevant first (the shared comparator, so the order is the
  // party page's).
  const boxes = useMemo(
    () => Object.entries(balances)
      .filter(([, v]) => v !== 0)
      .map(([asset, balance]) => ({ asset, balance }))
      .sort(compareRelevance),
    [balances],
  );
  // The most relevant box is picked ONCE, on first read — never after a
  // posting, when the next thing to convert is the operator's decision.
  const autoPicked = useRef(false);
  useEffect(() => {
    if (autoPicked.current || !q.data) return;
    autoPicked.current = true;
    if (boxes[0]) setFromAsset(boxes[0].asset);
  }, [q.data, boxes]);

  // The to asset cannot be the from asset: picking the to's asset as from
  // clears the to. In the click, not an effect — a render with from = to
  // would seat the balance in a pair of one asset and leave a ghost amount
  // behind once the to is cleared.
  const pickFrom = (asset: string) => {
    setFromAsset(asset);
    setToAsset((t) => (t && sameAsset(t, asset) ? null : t));
  };

  const balance = fromAsset ? (balances[fromAsset] ?? 0) : 0;
  const pairKnown = !!fromAsset && !!toAsset;
  // THE BALANCE IS THE ANCHOR. The from amount is prefilled with the WHOLE
  // balance the moment a from asset is chosen — converting all of it is the
  // common case — through preset(), so it counts as typed first: a rate typed
  // after it moves the to amount, never the balance (12 Sept). Re-anchored
  // when the from asset or its balance changes, and once more when the to
  // asset completes the pair.
  //
  // The reset that used to come first on a half pair is gone (12 Sept, later
  // the same day): the calculator now seats an amount by ASSET while one side
  // is still unpicked and carries it as the pair forms, so with only the from
  // asset known the one box it can hold is the from amount — the very box
  // preset() overwrites — and clearing it first changed nothing. `pairKnown`
  // stays in the key on purpose: re-running preset() as the pair forms is
  // what puts the whole balance back if a different figure was typed while
  // the to asset was still unpicked — the behaviour this screen was signed
  // off with. `toAsset` is NOT a key: switching the to asset on a full pair
  // must keep a typed from amount (the calculator carries it to its new box);
  // presetting there would snap it back to the balance.
  useEffect(() => {
    if (!fromAsset) return;
    math.preset({ asset: fromAsset }, balance !== 0 ? ledgerText(Math.abs(balance), fromAsset) : '');
  }, [fromAsset, balance, pairKnown]);   // eslint-disable-line react-hooks/exhaustive-deps

  const fromText = fromAsset ? math.amountOf(fromAsset) : '';
  const toText   = toAsset   ? math.amountOf(toAsset)   : '';
  const fromN = Number(fromText) || 0;
  const toN   = Number(toText)   || 0;
  const rateN = Number(math.rate) || 0;
  const fromWords = amountProblem(fromText);
  const toWords   = amountProblem(toText);
  const rateProblemWords = rateProblem(math.rate);
  const over = !unknown && fromAsset && fromN > Math.abs(balance) ? fromN - Math.abs(balance) : 0;
  // WHICH BOX MOVES is the calculator's call (math.isDerived / math.rateIsDerived,
  // 12 Sept 2026), never worked out here: that box's label says "auto" and its
  // figure is muted, so the operator sees at a glance which of the three the
  // other two are filling. It stays editable — typing in it makes it a fact.
  const fromAuto = !!fromAsset && math.isDerived(fromAsset);
  const toAuto   = !!toAsset   && math.isDerived(toAsset);

  const plan = useMemo(() => {
    if (unknown || !fromAsset || !toAsset || balance === 0 || fromN <= 0 || toN <= 0 || rateN <= 0 || fromWords || toWords || rateProblemWords) return null;
    return deriveConversion({ balance, fromAsset, toAsset, fromAmount: fromN, toAmount: toN, rate: math.rate });
  }, [unknown, fromAsset, toAsset, balance, fromN, toN, rateN, fromWords, toWords, rateProblemWords, math.rate]);

  // OFF-BAND — the shared table (lib/trade-math.ts, sweep item 24), keyed by
  // the oriented pair the calculator holds: a flipped rate (0.27 where 3.67
  // was asked) is named with its reciprocal. Warn, never block; a rate the
  // calculator worked out from the two amounts says so, since then it is an
  // amount that is wrong, not the rate.
  const band = useMemo(() => {
    if (!pairKnown || rateProblemWords) return null;
    const w = bandWords(math.base, math.quote, rateN);
    return w;
  }, [pairKnown, rateN, rateProblemWords, math.base, math.quote]);
  // The band's sentence is the table's (lib/trade-math.ts) and runs long: its first sentence is the amber line, "Upside
  // down? 1 ÷ …" and the rest fold behind Details. It stands under the three boxes, not inside the Rate column — in a
  // 230px column the whole sentence was five lines and pushed its neighbours. A rate the calculator WORKED OUT from the two
  // amounts says so on the line itself: then it is an amount that is wrong, and "check them" is what stops him fixing the rate.
  const bandSaid = band ? sayFirst(band) : null;
  const bandDerived = math.derived === 'rate';

  // ── submit ───────────────────────────────────────────────────────────────
  // THE DEAL ID IS THE IDEMPOTENCY KEY, SO IT MUST SURVIVE A RETRY (NewTrade's
  // rule): minted once, held in a ref, cleared on success, re-minted when the
  // day it carries changes and nothing landed.
  const dealIdRef = useRef<string | null>(null);
  const { twin: repeat, clearTwin, guard } = useRepeatGuard();
  const [posted, setPosted] = useState<{
    at: number; dealId: string; deduped: boolean; asOf: string | null;
    /** The BFF's own word that both legs were netted (`converted: true`). A
     *  route that did not net them booked an ordinary settle-later trade,
     *  and the success line must not call that a conversion. */
    converted: boolean;
    name: string; from: string; to: string; fromAmount: number; toAmount: number; fromAfter: number; toAfter: number;
  } | null>(null);

  const post = async (confirmRepeat: boolean, confirmOver = false) => {
    if (!plan || !fromAsset || !toAsset) throw new Error('nothing to convert');
    dealIdRef.current ??= `DEAL-${whenIso ? dubaiDate(whenIso) : dubaiDate()}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
    const dealId = dealIdRef.current;
    const data = await api.bookTrade({
      entity,
      direction:  plan.direction,
      party_slug: slug,
      // Agreed and netted in one breath: no drawer, nothing awaiting.
      unsettled:         true,
      settle_on_account: true,
      amount_take: plan.take,
      amount_give: plan.give,
      rate:        plan.rate,
      deal_id:     dealId,
      // Only what the operator typed (14 Sept 2026, §5.8): the BFF writes
      // its own "converted on account" marker with the rate.
      notes:       notes.trim() || undefined,
      effective_at:   whenIso || undefined,
      confirm_repeat: confirmRepeat || undefined,
      confirm_over_balance: confirmOver || undefined,
    });
    // What the box will read once the books answer — projected from the
    // balances this plan was derived on. The success line prefers the LIVE
    // figure once the party has been re-read (see `posted.at`).
    // The box moves by −sign·fromAmount in the from asset and +sign·toAmount
    // in the to asset — the rule deriveConversion() encodes as take/give.
    const fromAfter = balance - plan.sign * fromN;
    const toAfter   = (balances[toAsset] ?? 0) + plan.sign * plan.converted;
    return {
      data, dealId, asOf: whenIso || null, name: q.data ? partyName(q.data.party) : slug,
      from: fromAsset, to: toAsset, fromAmount: fromN, toAmount: plan.converted, fromAfter, toAfter,
    };
  };
  const submit = useMutation({
    mutationFn: (opts?: { confirmRepeat?: boolean; confirmOver?: boolean }) => guard(() => post(opts?.confirmRepeat === true, opts?.confirmOver === true)),
    onSuccess: ({ data, dealId, ...facts }) => {
      clearTwin();
      dealIdRef.current = null;
      setPosted({ at: Date.now(), dealId: data.deal_id ?? dealId, deduped: !!data.deduped, converted: data.converted === true, ...facts });
      // A clean slate: the box just converted is gone or changed, and the next
      // conversion — if any — is a new decision, not a re-run of this one.
      setFromAsset(null); setToAsset(null); math.reset(); setWhen(''); setNotes('');
      tg()?.HapticFeedback.notificationOccurred('success');
    },
    onError: (e) => { if (!isSentinel(e)) tg()?.HapticFeedback.notificationOccurred('error'); },
  });
  const onWhenChange = (v: string) => {
    setWhen(v);
    const mayHold = submit.isPending || (submit.isError && !nothingLanded(submit.error));
    if (!mayHold) { dealIdRef.current = null; clearTwin(); }
  };

  // Only with nothing cached (see the route above): the same read on /swap, where the form is a panel with the list beside it.
  if (q.isError && !q.data) return <ReadFailed what="this party" error={q.error} onRetry={() => void q.refetch()} tone="bad" />;
  if (!q.data)   return <Pending />;

  const name = partyName(q.data.party);
  const partyHref = `/parties/${encodeURIComponent(q.data.party.slug)}${entity === 'dubaiotc' ? '' : `?entity=${entity}`}`;
  // A RAIL IS NOT A PARTY HERE — the rule PartyDetail's isOwnAccount keeps.
  // The party page shows no convert link on a rail, but a pasted
  // /parties/<rail-slug>/convert would load the rail's balances as boxes and
  // post a settle-on-account trade against an account no party holds.
  if (q.data.party.party_type === 'own_account') {
    return (
      <StateLine tone="warn" action={<Link to={partyHref} className={TAP}>Open account →</Link>}>
        {name} is a desk account — no box to convert.
      </StateLine>
    );
  }
  // The success line reads the LIVE box once the party has been re-read after
  // the posting (every mutation refreshes ['party']); until then, the projection.
  const live = posted && q.dataUpdatedAt > posted.at;
  const fromAfter = posted ? (live ? (q.data.balances[posted.from] ?? 0) : posted.fromAfter) : 0;
  const toAfter   = posted ? (live ? (q.data.balances[posted.to]   ?? 0) : posted.toAfter)   : 0;

  return (
    <>
      {boxes.length === 0 && !posted ? (
        // "Nothing to convert." is a claim from a read that SUCCEEDED; after a failed refresh the warning below is all that is said.
        unknown ? null : <EmptyState action={<Link to={partyHref} className={TAP}>Open {name} →</Link>}>Nothing to convert.</EmptyState>
      ) : (
        <>
          <Field label="From">
            {boxes.length === 0
              ? <p className="text-meta text-tg-hint">Nothing to convert.</p>
              : (
                <div className="flex flex-wrap gap-1.5 lg:gap-1">
                  {boxes.map((b) => (
                    <Chip key={b.asset} active={!!fromAsset && sameAsset(fromAsset, b.asset)} onClick={() => pickFrom(b.asset)}>
                      {displayAsset(b.asset)} · {boxWords(b.balance, b.asset)}
                    </Chip>
                  ))}
                </div>
              )}
          </Field>

          {/* The desk's everyday three as chips, every other asset behind
              "more…" (AssetPicker, 12 Sept 2026) — folded codes, a party's box
              speaks USDT. The from asset cannot be picked here; the other
              direction is pickFrom's. */}
          <Field label="To">
            <AssetPicker
              value={toAsset ?? ''}
              onChange={(a) => setToAsset(a)}
              options={BOX_ASSETS}
              exclude={(a) => !!fromAsset && sameAsset(a, fromAsset)}
            />
          </Field>

          {/* THREE LIVE BOXES — asset-1, asset-2 & rate, the operator's order
              (12 Sept). Each passes what was typed straight to the calculator,
              untrimmed and uncoerced, so a paste from Telegram lands as it
              was; the calculator fills whichever box was not typed, and that
              box reads "auto" with its figure muted (12 Sept, later). One
              hint at most, never a sentence, so the three sit on one
              baseline (§5.8).

              THE PHONE (19 Sept 2026, the phone-layout pass): the two
              amounts are the related pair, so they sit side by side — from
              amount → to amount, reading left to right — and the Rate takes
              the whole line beneath them, where its hint ("INR per AED ·
              auto") and the rate-both-ways line have room. Three across
              would give each box ~100px at 375px; one under another would
              lose the pair. At lg+ the three columns are as they were. */}
          <FieldRow cols={3} className="max-lg:grid-cols-2">
            <Field
              label={fromAsset ? `Amount in ${displayAsset(fromAsset)}` : 'Amount'}
              hint={fromAuto ? 'auto' : undefined}
            >
              <input
                className={`input${fromAuto ? ' text-tg-hint' : ''}`}
                value={fromText}
                onChange={(e) => { if (fromAsset) math.setAmount(fromAsset, e.target.value); }}
                inputMode="decimal"
                disabled={!fromAsset}
              />
              <ErrorLine>{fromWords}</ErrorLine>
              {!fromWords && over > 0 && fromAsset && (
                <WarnLine>Over by {fmtAmount(over, fromAsset)} — the box flips.</WarnLine>
              )}
            </Field>

            <Field
              label={toAsset ? `Amount in ${displayAsset(toAsset)}` : 'Amount it becomes'}
              hint={toAuto ? 'auto' : undefined}
            >
              <input
                className={`input${toAuto ? ' text-tg-hint' : ''}`}
                value={toText}
                onChange={(e) => { if (toAsset) math.setAmount(toAsset, e.target.value); }}
                inputMode="decimal"
                disabled={!pairKnown}
              />
              <ErrorLine>{toWords}</ErrorLine>
            </Field>

            <Field
              label="Rate"
              hint={pairKnown ? `${math.rateWords}${math.rateIsDerived ? ' · auto' : ''}` : undefined}
              className="max-lg:col-span-2"
            >
              <input
                className={`input${math.rateIsDerived ? ' text-tg-hint' : ''}`}
                value={math.rate}
                onChange={(e) => math.setRate(e.target.value)}
                inputMode="decimal"
                disabled={!pairKnown}
              />
              <ErrorLine>{rateProblemWords}</ErrorLine>
              {!rateProblemWords && rateN > 0 && pairKnown && (
                <p className="text-meta text-tg-hint mt-1">
                  {/* each way is one piece: in the laptop's 230px Rate column the line breaks BETWEEN the two, never inside one */}
                  <span className="whitespace-nowrap">1 {displayAsset(math.base)} = {fmtRate(rateN, math.quote)} {displayAsset(math.quote)}</span>
                  {' · '}<span className="whitespace-nowrap">1 {displayAsset(math.quote)} = {fmtRate(1 / rateN, math.base)} {displayAsset(math.base)}</span>
                </p>
              )}
            </Field>
          </FieldRow>
          {bandSaid && (
            <StateLine tone="warn" more={bandSaid.rest || undefined}>
              {bandSaid.head}{bandDerived ? ' Worked out from the two amounts — check them.' : ''}
            </StateLine>
          )}

          {/* WHAT WILL BE BOOKED, before it is: the two box lines, before →
              after (§5.8 — the only read-back of the derived direction). */}
          {plan && fromAsset && toAsset && (
            <div className="rounded-lg border border-stroke-soft bg-panel px-3 py-2 space-y-0.5">
              <BoxLines asset={fromAsset} before={balance} after={balance - plan.sign * fromN} />
              <BoxLines asset={toAsset} before={balances[toAsset] ?? 0} after={(balances[toAsset] ?? 0) + plan.sign * plan.converted} />
            </div>
          )}

          {/* AS OF — the chip row every entry screen carries since 14 Sept
              2026 (sweep item 23): Now is an explicit choice, the picker
              appears only when asked for, and the chips render the echo and
              the back-date words themselves. onWhenChange keeps the deal id
              rule. (21 Oct 2026 audit: it sat alone in a two-column FieldRow,
              so its echo line floated mid-card; the chips stand on their own.) */}
          <AsOfChips value={when} onChange={onWhenChange} disabled={submit.isPending} />

          {/* Notes under More: 1 conversion all-time, its note was "settled" (§5.8). */}
          <MoreDetails label="More">
            <Field label="Notes">
              <textarea className="input h-16" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to remember" />
            </Field>
          </MoreDetails>

          <div className="lg:flex lg:justify-end lg:border-t lg:border-stroke-soft lg:pt-5">
            <button
              className="btn-primary w-full lg:w-auto lg:px-6"
              disabled={submit.isPending || !plan || !!whenWords}
              onClick={() => submit.mutate({})}
            >
              {submit.isPending ? 'Booking…'
                : `Convert${plan && toAsset ? ` to ${displayAsset(toAsset)}` : ''}${whenIso && !whenWords ? ` as of ${fmtDay(whenIso)}` : ''}`}
            </button>
          </div>
        </>
      )}

      {/* A refresh that FAILED while the party is in hand (a posting refreshes it): the form and the outcome below stay, and this is
          the warning beside them — amber, with Try again (a retry that reads the party leaves the outcome as it was). */}
      {q.isError && <ReadFailed what="this party" error={q.error} onRetry={() => void q.refetch()} />}
      {submit.isSuccess && posted && (
        posted.deduped ? (
          <StateLine
            tone="warn"
            more={<>Booked earlier under <span className="font-mono whitespace-nowrap">{posted.dealId}</span> — check the party page.</>}
            action={<Link to={partyHref} className={TAP}>Open party →</Link>}
          >
            Already booked — nothing changed.
          </StateLine>
        ) : !posted.converted ? (
          // Booked, but the books did not say the legs were netted — it is
          // sitting on Settle later as an ordinary settle-later trade. THE GUARD STAYS A LINE: the sentence that said it
          // all (22 Oct 2026: 31 words) is behind Details; the line says what to do before the next tap.
          <StateLine
            tone="warn"
            more={<>Booked as <span className="font-mono whitespace-nowrap">{posted.dealId}</span>, but the books did not confirm it was netted on account — it may be waiting as an ordinary settle-later trade.</>}
            action={<Link to="/settle" className={TAP}>Settle later →</Link>}
          >
            Booked, but not confirmed as netted — check before converting again.
          </StateLine>
        ) : (
          <div className="space-y-0.5">
            <p className="text-body text-pos">
              ✓ Converted {fmtAmount(posted.fromAmount, posted.from)} {displayAsset(posted.from)} → {fmtAmount(posted.toAmount, posted.to)} {displayAsset(posted.to)}
            </p>
            <p className="text-meta text-tg-hint">
              for {posted.name}{posted.asOf ? ` · as of ${fmtDay(posted.asOf)}` : ''} · <span className="font-mono whitespace-nowrap">{posted.dealId}</span>
              {' · '}<Link to={partyHref} className={TAP}>Open party →</Link>
            </p>
            {/* The two boxes NOW — the projection until the party is re-read, the live box after. While the refresh is failed
                neither is claimed: the projection says "square" of a box nobody has read since the posting. */}
            {!unknown && (
              <p className="text-meta text-tg-hint">
                {displayAsset(posted.from)} · {boxWords(fromAfter, posted.from)} · {displayAsset(posted.to)} · {boxWords(toAfter, posted.to)}
              </p>
            )}
          </div>
        )
      )}
      {submit.isError && !isSentinel(submit.error) && <ServerLine error={submit.error} />}
      {/* The Worker refuses a conversion larger than the balance it converts:
          the excess would flip the box the other way inside a "settled" deal.
          The operator can say that is intended (11 Sept 2026). */}
      {submit.isError && submit.error instanceof ApiError && submit.error.code === 'over_balance' && (
        <button className="btn-secondary text-body w-full lg:w-auto" disabled={unknown} onClick={() => submit.mutate({ confirmOver: true })}>
          Convert anyway — flip the balance the other way
        </button>
      )}
      {repeat && (
        <RepeatPanel
          twin={repeat}
          pending={submit.isPending || !!whenWords || unknown}
          onAlreadyBooked={() => { clearTwin(); nav(-1); }}
          // A settle-on-account trade meets only the twin-trade guard — the
          // cross-shape guard is for settled trades that move a drawer.
          onPostAnyway={() => submit.mutate({ confirmRepeat: true })}
        />
      )}
    </>
  );
}
